'use client'

import React, { createContext, ReactElement, useMemo, useEffect, useState } from 'react'

import type { Devices, DeviceType } from 'src/types/device'
import { BreakpointSizes } from 'src/types/device'
import { getTimezoneOffset, deviceToBreakpoint } from './helpers'
import { isApple } from 'src/utils/isApple'

const DeviceContext = createContext<DeviceType | null>(null)

const DeviceProvider = ({
  children,
  value,
}: {
  children: ReactElement
  value: {
    activeBreakpoint: BreakpointSizes
    device: Devices
    ip: string
    metadevice?: Devices
    isBot: boolean
  }
}) => {
  if (value === null) {
    throw new Error('DeviceContext provider can not have a null value')
  }

  const { device, ip, metadevice, isBot, activeBreakpoint: initialBreakpoint } = value

  const [activeBreakpoint, setActiveBreakpoint] = useState(
    initialBreakpoint ?? deviceToBreakpoint(device)
  )

  const [colorDepth, setColorDepth] = useState<string | null>(null)
  const [javaEnabled] = useState<boolean>(false)
  const [language, setLanguage] = useState<string | null>(null)
  const [timeZone, setTimeZone] = useState<number>(0)

  useEffect(() => {
    const mediaQueries = {
      mobile: window.matchMedia(`(max-width: ${BreakpointSizes.M - 1}px)`),
      tablet: window.matchMedia(
        `(min-width: ${BreakpointSizes.M}px) and (max-width: ${BreakpointSizes.L - 1}px)`
      ),
      desktop: window.matchMedia(`(min-width: ${BreakpointSizes.L}px)`),
    }

    const handleMediaQuery = (e: MediaQueryListEvent | MediaQueryList) => {
      if (!e.matches) return

      let breakpoint: BreakpointSizes

      if (e === mediaQueries.mobile || e.media === mediaQueries.mobile.media) {
        breakpoint = BreakpointSizes.S
      } else if (e === mediaQueries.tablet || e.media === mediaQueries.tablet.media) {
        breakpoint = BreakpointSizes.M
      } else {
        breakpoint = BreakpointSizes.L
      }

      setActiveBreakpoint(breakpoint)
    }

    Object.values(mediaQueries).forEach(mq => {
      mq.addEventListener('change', handleMediaQuery)
      handleMediaQuery(mq)
    })

    setColorDepth(`${window.screen.colorDepth}`)
    setLanguage(window.navigator.language)
    setTimeZone(getTimezoneOffset())

    return () => {
      Object.values(mediaQueries).forEach(mq => {
        mq.removeEventListener('change', handleMediaQuery)
      })
    }
  }, [])

  const deviceContextValue = useMemo(
    () => ({
      device,
      ip,
      metadevice,
      isBot,
      activeBreakpoint,
      colorDepth,
      javaEnabled,
      language,
      timeZone,
      get screenWidth() {
        if (typeof window === 'undefined') {
          return 0
        }
        return window.innerWidth
      },

      get screenHeight() {
        if (typeof window === 'undefined') {
          return 0
        }

        return window.innerHeight
      },
      get isApple() {
        if (typeof window === 'undefined') {
          return false
        }
        return isApple()
      },
    }),
    [device, ip, metadevice, isBot, activeBreakpoint, colorDepth, javaEnabled, language, timeZone]
  )

  return <DeviceContext.Provider value={deviceContextValue}>{children}</DeviceContext.Provider>
}

export { DeviceContext, DeviceProvider }
